/* Shared "How to Play" dialog shell. Owns the native <dialog>
   plumbing both games need:

   - showModal()-driven open + cancel-by-Esc
   - manual backdrop-click close (the spec doesn't include it, so we
     check `e.target === dialog` since the dialog only receives the
     click directly when the user clicked its ::backdrop pseudo)
   - first-visit auto-open: ~Ns after mount, if no first-visit token
     in localStorage, stamp the current ISO timestamp and pop the
     modal so newcomers see the rules before they start playing.
     Suppress on subsequent visits and on storage-access failure
     (private mode, denied) so we never re-pop on reload.
   - trigger collapse to a circular "?" icon at ≤500px viewport
     widths, via the `howto-trigger` base class + matching CSS in
     styles/components/howToPlay.css. The shell auto-wraps the
     consumer's label in <span class="howto-trigger__label"> so the
     CSS can hide it without each game re-implementing the wrapper.
   - `tabIndex={-1}` by default — the trigger is a secondary
     affordance and shouldn't grab the tab cursor while the player
     is in the playfield. Override per game if needed.

   Each game wraps this with its own rules content + BEM class
   prefix. The daily-game uses prefix "howtoplay"; Match uses
   "match-howtoplay". Skin (background, border, font, padding) lives
   per game; layout mechanics + responsive collapse + tab order live
   in the shell. */

import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";

export interface HowToPlayDialogProps {
  /** BEM class prefix used to derive every dialog-shell class:
   *  trigger=`${prefix}__trigger`, dialog=`${prefix}`,
   *  close=`${prefix}__close`, body=`${prefix}__body`.
   *  The consumer's body must wire `<h2 id={`${prefix}-title`}>` so
   *  the dialog's aria-labelledby resolves. */
  classPrefix: string;
  /** localStorage key gating the first-visit auto-open. */
  storageKey: string;
  /** Auto-open delay (ms) on first visit. Default 2000. */
  firstVisitOpenDelayMs?: number;
  /** Content rendered inside the trigger button. The shell wraps
   *  this in a `<span class="howto-trigger__label">` so the
   *  responsive collapse rule can hide it without consumer help. */
  triggerLabel: ComponentChildren;
  /** Optional tabIndex on the trigger. Defaults to -1 so the
   *  trigger doesn't grab tab focus from the playfield. Pass `0`
   *  (or any non-negative value) to opt the trigger back into the
   *  default tab order. */
  triggerTabIndex?: number;
  /** Dialog body — the actual rules content. Must include an
   *  `<h2 id={`${classPrefix}-title`}>` for aria-labelledby. */
  children: ComponentChildren;
}

export function HowToPlayDialog(props: HowToPlayDialogProps) {
  const {
    classPrefix,
    storageKey,
    firstVisitOpenDelayMs = 2000,
    triggerLabel,
    triggerTabIndex = -1,
    children,
  } = props;
  const ref = useRef<HTMLDialogElement>(null);

  const open = () => ref.current?.showModal();
  const close = () => ref.current?.close();
  const onBackdropClick = (e: MouseEvent) => {
    if (e.target === ref.current) close();
  };

  useEffect(() => {
    const id = window.setTimeout(() => {
      let stored: string | null = null;
      try {
        stored = localStorage.getItem(storageKey);
      } catch {
        return;
      }
      if (stored != null) return;
      try {
        localStorage.setItem(storageKey, new Date().toISOString());
      } catch {
        return;
      }
      ref.current?.showModal();
    }, firstVisitOpenDelayMs);
    return () => window.clearTimeout(id);
  }, [storageKey, firstVisitOpenDelayMs]);

  const titleId = `${classPrefix}-title`;
  return (
    <>
      <button
        type="button"
        class={`howto-trigger ${classPrefix}__trigger`}
        onClick={open}
        aria-label="How to play"
        title="How to play"
        tabIndex={triggerTabIndex}
      >
        <span class="howto-trigger__label">{triggerLabel}</span>
      </button>
      <dialog
        ref={ref}
        class={`howto-dialog ${classPrefix}`}
        aria-labelledby={titleId}
        onClick={onBackdropClick}
      >
        <button
          type="button"
          class={`howto-dialog__close ${classPrefix}__close`}
          onClick={close}
          aria-label="Close"
        >
          <span aria-hidden="true">×</span>
        </button>
        <div class={`howto-dialog__body ${classPrefix}__body`}>{children}</div>
      </dialog>
    </>
  );
}
